/**
 * Supabase Auth returns this whenever there is no session to check at all --
 * an ordinary anonymous visitor with no auth cookies (or a JWT-visible-but-no-
 * active-session case the SDK already self-heals by clearing its own
 * storage). This is NOT a broken/revoked session: `getUser()` returns it via
 * its normal result object (`{ data: { user: null }, error }`), not a thrown
 * exception, for the exact case of "no access_token and no custom auth
 * header" (see `@supabase/auth-js` GoTrueClient `_getUser`). It must be
 * treated identically to a clean `{ user: null, error: null }` result, not
 * swept into the transient-failure catch-all (which would incorrectly skip
 * the protected-route redirect) or the invalid-session branch (which would
 * incorrectly show a "session ended" message to someone who was never
 * signed in).
 */
export function isSessionMissingError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { name?: string; message?: string };
  if (e.name === 'AuthSessionMissingError') return true;
  if (typeof e.message === 'string' && e.message.includes('Auth session missing')) return true;
  return false;
}

/** Supabase Auth returns this when JWT is valid-shaped but session row is gone (stale cookies / revoked / reset). */
export function isInvalidSessionError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { code?: string; message?: string };
  if (e.code === 'session_not_found') return true;
  if (typeof e.message === 'string' && e.message.includes('session_id claim')) return true;
  if (typeof e.message === 'string' && e.message.includes('does not exist')) return true;
  return false;
}

/** Stale or unknown refresh token in storage (logged-out / new visitor should not crash). */
export function isRefreshTokenInvalidError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { code?: string; message?: string };
  if (e.code === 'refresh_token_not_found') return true;
  if (typeof e.message === 'string' && e.message.includes('Invalid Refresh Token')) return true;
  if (typeof e.message === 'string' && e.message.includes('Refresh Token Not Found')) return true;
  return false;
}

/** Supabase internal lock conflict — safe to retry or ignore. */
export function isLockConflictError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { name?: string; message?: string };
  if (e.name === 'AbortError') return true;
  return (
    typeof e.message === 'string' &&
    (e.message.includes('Lock broken') ||
      e.message.includes('steal') ||
      e.message.includes('steal option') ||
      e.message.includes('lock'))
  );
}

/** Auth user removed server-side while JWT cookies still exist locally. */
export function isUserNotFoundError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { code?: string; message?: string };
  if (e.code === 'user_not_found') return true;
  if (typeof e.message === 'string' && e.message.includes('User from sub claim')) return true;
  if (typeof e.message === 'string' && e.message.includes('JWT does not exist')) return true;
  if (typeof e.message === 'string' && e.message.toLowerCase().includes('user not found')) {
    return true;
  }
  return false;
}

/** Supabase rate limit error. */
export function isRateLimitError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { message?: string; status?: number };
  return (
    e.status === 429 ||
    (typeof e.message === 'string' &&
      (e.message.includes('rate limit') || e.message.includes('Request rate limit')))
  );
}
